import { createContext, useContext, useMemo, useState } from 'react';
import useCitiesActions from '../hooks/APIHooks/users/useCitiesActions';
import { City, CityReqBody } from '../types/cities';

// datatype of the context declared here
interface ICityContext {
  targetedCity: City | null;
  cityList: Array<City>;
  selectedCities: Array<CityReqBody>;
  cityAddedProvinceOptions: Array<string>;
  filterProvince: string;
  searchText: string;
  showDeleteModal: boolean;
  showEditModal: boolean;
  isResetFilter: boolean;
  setTargetedCity: React.Dispatch<React.SetStateAction<City | null>>;
  setSelectedCities: React.Dispatch<React.SetStateAction<Array<CityReqBody>>>;
  setFilterProvince: React.Dispatch<React.SetStateAction<string>>;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsResetFilter: React.Dispatch<React.SetStateAction<boolean>>;
  showDeleteCityModalHandler: (city: City) => void;
  showEditCityModalHandler: (city: City) => void;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

// creating the context here
const CityContext = createContext<ICityContext | null>(null);

const CityProvider = ({ children }: { children: React.ReactNode }) => {
  // integration of react hooks here
  const [targetedCity, setTargetedCity] = useState<City | null>(null);

  const [selectedCities, setSelectedCities] = useState<Array<CityReqBody>>([]);

  const [filterProvince, setFilterProvince] = useState<string>('All');
  const [searchText, setSearchText] = useState<string>('');

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isResetFilter, setIsResetFilter] = useState<boolean>(false);

  // integration of custom API hooks here
  const { getCityListHandler } = useCitiesActions('cityList');

  console.log('CITY LIST: ', getCityListHandler.data?.data);

  // extracting province names for filter city by province name options here here
  const cityAddedProvinceOptions = useMemo(() => {
    if (!getCityListHandler.data?.is_success) return [];

    const provinces = getCityListHandler.data.data.map(
      (city) => city.province_name
    );

    const provinceSet = new Set(provinces);
    const uniqueProvinces = Array.from(provinceSet);

    return uniqueProvinces.sort();
  }, [getCityListHandler.data]);

  // filtering cities here
  const cityList = useMemo(() => {
    if (!getCityListHandler.data?.is_success) return [];

    return getCityListHandler.data.data
      .filter((city) =>
        filterProvince !== 'All'
          ? city.province_name.toLowerCase() === filterProvince.toLowerCase()
          : city
      )
      .filter((city) =>
        searchText
          ? city.city_name.toLowerCase().includes(searchText.toLowerCase())
          : city
      );
  }, [getCityListHandler.data, filterProvince, searchText]);

  // handler function to show delete city modal declared here
  const showDeleteCityModalHandler = (city: City) => {
    setTargetedCity(city);
    setShowDeleteModal(true);
  };

  const showEditCityModalHandler = (city: City) => {
    setTargetedCity(city);
    setShowEditModal(true);
  };

  // rendering the context provider here
  return (
    <CityContext.Provider
      value={{
        targetedCity,
        cityList,
        selectedCities,
        cityAddedProvinceOptions,
        filterProvince,
        searchText,
        showDeleteModal,
        showEditModal,
        isResetFilter,
        setTargetedCity,
        setSelectedCities,
        setFilterProvince,
        setSearchText,
        setShowDeleteModal,
        setShowEditModal,
        setIsResetFilter,
        showDeleteCityModalHandler,
        showEditCityModalHandler,
        isError: getCityListHandler.isError,
        isLoading: getCityListHandler.isLoading,
        isSuccess: getCityListHandler.isSuccess,
      }}
    >
      {children}
    </CityContext.Provider>
  );
};

export default CityProvider;

// custom hook to access the city context declared here
export const useCityContext = () => {
  const context = useContext(CityContext);

  if (!context) {
    throw new Error('useCityContext must be within CityProvider!');
  }

  return context;
};
